import React from "react";

import { Grid, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { makeStyles } from "tss-react/mui";
import { VictoryArea, VictoryAxis, VictoryChart, VictoryLine } from "victory";

import { InvestmentChartData } from "libLegacy/api/models";
import LoadingDots from "components/libComponents/LoadingDots/LoadingDots";

import { INVESTMENTS_CHART_PADDING, AXIS_STYLE } from "./chartStyles";
import { ptBR } from "date-fns/locale";
import { ChartCaption } from "./ChartCaption";

const useStyles = makeStyles()((theme) => ({
  outer: {
    height: "100%",
    position: "relative",
  },
  overlay: {
    position: "absolute",
    height: "100%",
    width: "100%",
  },
  legend: {
    gap: theme.spacing(2),
    fontSize: "12px",
    position: "absolute",
    bottom: 0,
  },
}));

const getTickValuesLabelFromDataset = (dataset) => {
  const labelRatio = 15;

  // only displays ONE tick every labelRatio interval
  const valuesToDisplayOnAxis = dataset.filter(
    (item, i) => i % labelRatio === 0
  );
  const formattedTickValues = valuesToDisplayOnAxis.map(({ value }) =>
    Math.round(value)
  );
  return formattedTickValues;
};

const formatYLabel = (yValue: string) => {
  return (+yValue).toLocaleString(ptBR.code, {
    style: "currency",
    currency: "BRL",
    notation: "compact",
    compactDisplay: "long",
  });
};

// Victory treats string xValues as categories and applies different policies to them.
// This converts our dates into numbers for more safety
const formatXValue = ({ time }) => new Date(time).valueOf();

const formatYValue = ({ value }) => +value;

const formatXLabel = (xValue) => {
  const refDate = new Date(xValue);
  const month = refDate.toLocaleDateString(ptBR.code, { month: "short" });
  const year = refDate.toLocaleDateString(ptBR.code, { year: "2-digit" });

  return `${month} ${year}`;
};

export const ChartPayback = ({
  estimatedPayback,
  realPayback,
  investedAmount,
  loading,
}: ChartPaybackProps) => {
  const { classes } = useStyles();
  const theme = useTheme();

  return (
    <Grid container className={classes.outer}>
      <svg width="0" height="0" viewBox="0 0 0 0">
        <defs>
          <linearGradient
            id="gradient-payback"
            x1="112"
            y1="0"
            x2="112"
            y2="220"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor={theme.palette.charts.primary} />
            <stop
              offset="1"
              stopColor={theme.palette.charts.primary}
              stopOpacity="0.22"
            />
          </linearGradient>
        </defs>
      </svg>
      <Typography variant="body1">Payback</Typography>
      <VictoryChart padding={INVESTMENTS_CHART_PADDING}>
        <VictoryAxis
          style={AXIS_STYLE}
          tickFormat={formatXLabel}
          fixLabelOverlap
        />
        <VictoryAxis
          dependentAxis
          tickFormat={formatYLabel}
          style={AXIS_STYLE}
          tickValues={
            estimatedPayback
              ? getTickValuesLabelFromDataset(estimatedPayback)
              : []
          }
        />
        {/* ESTIMATED PAYBACK */}
        {estimatedPayback && investedAmount && (
          <VictoryArea
            data={estimatedPayback}
            style={{ data: { fill: `url(#gradient-payback)` } }}
            y={formatYValue}
            x={formatXValue}
          />
        )}

        {/* REAL PAYBACK */}
        {realPayback && (
          <VictoryLine
            data={realPayback}
            style={{ data: { stroke: theme.palette.charts.secondary } }}
            x={formatXValue}
            y={formatYValue}
          />
        )}
      </VictoryChart>
      <Grid container className={classes.legend} justifyContent="center">
        <ChartCaption label="Real" color={theme.palette.charts.secondary} />
        <ChartCaption label="Estimado" color={theme.palette.charts.primary} />
      </Grid>

      {loading && (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          className={classes.overlay}
        >
          <LoadingDots />
        </Grid>
      )}
    </Grid>
  );
};

export type ChartPaybackProps = {
  estimatedPayback?: Array<InvestmentChartData>;
  realPayback?: Array<InvestmentChartData>;
  investedAmount?: string;
  loading: boolean;
};
